import without from 'lodash/fp/without';
import { concat } from 'lodash/fp';
import isEqual from 'lodash/fp/isEqual';

import { useTableDataState } from './table-data.state';
import create from 'zustand';

type ExpandedState = {
   expandedRows: number[];
   pendingExpandAll: boolean;
   expandCount: number;
   resetExpanded: () => void;
};

export const useExpandedState = create<ExpandedState>((set) => ({
   expandedRows: [],
   pendingExpandAll: false,
   expandCount: 0,
   resetExpanded: () => set({ expandedRows: [] }),
}));

export const useExpandedOnRow = (index: number) => {
   const expandedRows = useExpandedState(
      (state) => state.expandedRows,
      (oldExp, newExp) => !((oldExp.includes(index) && !newExp.includes(index)) || (!oldExp.includes(index) && newExp.includes(index)))
   );

   const isExpanded = expandedRows.includes(index);

   const toggleExpanded = (rowIndex: number) => {
      expandedRows.includes(rowIndex)
         ? useExpandedState.setState((state) => ({
              expandedRows: without([index])(state.expandedRows),
              expandCount: state.expandCount + 1,
           }))
         : useExpandedState.setState((state) => ({
              expandedRows: concat(index)(state.expandedRows),
              expandCount: state.expandCount + 1,
           }));
   };

   return { isExpanded, toggleExpanded };
};

export const useExpanded = () => {
   const expandedRows = useExpandedState((state) => state.expandedRows);
   const query = useTableDataState((state) => state.query);

   const allExpanded = Array.from(Array(query.pageSize).keys());

   const expandAll = () =>
      useExpandedState.setState((state) => ({
         expandedRows: allExpanded,
         expandCount: state.expandCount + 1,
      }));

   const collapseAll = () =>
      useExpandedState.setState((state) => ({
         expandedRows: [],
         expandCount: state.expandCount + 1,
      }));

   const isAllExpanded = isEqual(allExpanded)(expandedRows);
   const isAnyExpanded = expandedRows.length !== 0;

   return { expandAll, collapseAll, isAllExpanded, isAnyExpanded };
};
